import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Confirm, Dimmer, Dropdown, Icon, Image, Label, Loader, Message, Popup, Segment, TextArea,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { TournamentDto, TournamentPostDto, TournamentPostVisibility } from '../../app/models/Tournament';

const REACTION_TYPES: string[] = ['👍', '❤️', '🔥', '👏', '😢'];
const REACTION_NAMES: Record<string, string> = {
  '👍': 'Like',
  '❤️': 'Love',
  '🔥': 'Fire',
  '👏': 'Clap',
  '😢': 'Sad',
};


const NAME_TO_EMOJI: Record<string, string> = {
  'Like': '👍',
  'Love': '❤️',
  'Fire': '🔥',
  'Clap': '👏',
  'Sad': '😢',
};

interface ReactionBarProps {
  reactions: { type: string; count: number }[];  
  currentUserReaction: string | null;
  onReact: (type: string) => void;  
}

function ReactionBar({ reactions, currentUserReaction, onReact }: ReactionBarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>

      {reactions.map(r => (
        <button
          key={r.type}
          onClick={() => onReact(r.type)} 
          style={{
            background: currentUserReaction === r.type ? '#e8f0fe' : '#f5f5f5',
            border: currentUserReaction === r.type ? '1px solid #4285f4' : '1px solid #e0e0e0',
            borderRadius: 12,
            padding: '2px 8px',
            cursor: 'pointer',
            fontSize: '0.82em',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
          }}
        >

          <span>{NAME_TO_EMOJI[r.type] || r.type}</span>
          <span style={{ color: '#555' }}>{r.count}</span>
        </button>
      ))}


      <Popup
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        trigger={
          <button
            onClick={() => setPickerOpen(v => !v)}
            style={{
              background: 'transparent',
              border: '1px dashed #ccc',
              borderRadius: 12,
              padding: '2px 8px',
              cursor: 'pointer',
              fontSize: '0.82em',
              color: '#888',
            }}
          >
            {currentUserReaction ? NAME_TO_EMOJI[currentUserReaction] : '＋'}
          </button>
        }
        content={
          <div style={{ display: 'flex', gap: 8, padding: 4 }}>
            {REACTION_TYPES.map(emoji => (
              <button
                key={emoji}
                title={REACTION_NAMES[emoji]}
                onClick={() => { 
                  onReact(REACTION_NAMES[emoji]);  
                  setPickerOpen(false); 
                }}
                style={{
                  background: currentUserReaction === REACTION_NAMES[emoji] ? '#e8f0fe' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.3em',
                  padding: '2px 4px',
                  borderRadius: 6,
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        }
        position="top center"
        on="click"
      />
    </div>
  );
}

// ── SingleReply ───────────────────────────────────────────────────────────────

interface SingleReplyProps {
  reply: any;
  postId: string;
}

const SingleReply = observer(function SingleReply({ reply, postId }: SingleReplyProps) {
  const { tournamentStore, userStore } = useStore();
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canDelete = reply.authorId === userStore.user?.userID;

  // Safe date formatting
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return format(date, 'dd MMM, HH:mm');
    } catch {
      return '';
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
      <Image
        avatar
        src={reply.authorImageUrl || '/assets/user.png'}
        as={Link}
        to={`/profiles/${reply.authorId}`}
        style={{ width: 26, height: 26, flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span style={{ fontWeight: 600, fontSize: '0.82em' }}>
            <Link to={`/profiles/${reply.authorId}`}>{reply.authorName}</Link>
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.75em', color: '#aaa' }}>
              {formatDate(reply.createdAt)}
            </span>
            {canDelete && (
              <Icon
                name="trash alternate"
                size="small"
                color="grey"
                style={{ cursor: 'pointer' }}
                onClick={() => setConfirmDelete(true)}
              />
            )}
          </div>
        </div>
        <p style={{ margin: '2px 0 4px', fontSize: '0.88em' }}>{reply.content}</p>
      </div>
      <Confirm
        open={confirmDelete}
        size="mini"
        content={t('tournament.chat.confirmDeleteReply')}
        cancelButton={t('teams.cancel')}
        confirmButton={<Button negative size="tiny" content={t('teams.remove')} />}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          // TODO: Implement delete reply in store
          setConfirmDelete(false);
        }}
      />
    </div>
  );
});

// ── SinglePost ────────────────────────────────────────────────────────────────

interface SinglePostProps {
  post: TournamentPostDto;
  canAdmin: boolean;
  canPost: boolean;
}

const SinglePost = observer(function SinglePost({ post, canAdmin, canPost }: SinglePostProps) {
  const { tournamentStore, userStore } = useStore();
  const { t } = useTranslation();
  const [replyInput, setReplyInput] = useState('');
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [repliesExpanded, setRepliesExpanded] = useState(false);

  const canDelete = post.authorId === userStore.user?.userID || canAdmin;

  // Zaštita - ako reactions ne postoji, koristi prazan niz
  const reactionsArray = post.reactions || [];
  
  const reactionMap = new Map<string, number>();
  reactionsArray.forEach(r => {
    reactionMap.set(r.type, (reactionMap.get(r.type) || 0) + 1);
  });
  const reactions = Array.from(reactionMap.entries()).map(([type, count]) => ({ type, count }));
  const currentUserReaction = reactionsArray.find(r => r.userId === userStore.user?.userID)?.type || null;

  // Zaštita - ako replies ne postoji, koristi prazan niz
  const repliesArray = post.replies || [];

  // Safe date formatting
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return format(date, 'dd MMM, HH:mm');
    } catch {
      return '';
    }
  };

  const handleAddReply = async () => {
    if (!replyInput.trim()) return;
    await tournamentStore.addReply(post.id, replyInput.trim());
    setReplyInput('');
    setShowReplyInput(false);
    if (!repliesExpanded) setRepliesExpanded(true);
    // Reload posts to show new reply
    await tournamentStore.loadPosts(post.contextType, post.contextId);
  };

  return (
    <div style={{ borderBottom: '1px solid #f0f0f0', padding: '12px 0', background: post.isPinned ? '#fffff0' : undefined }}>
      {/* Header */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Image
          avatar
          src={post.authorImageUrl || '/assets/user.png'}
          as={Link}
          to={`/profiles/${post.authorId}`}
          style={{ width: 36, height: 36, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Link to={`/profiles/${post.authorId}`} style={{ fontWeight: 700, fontSize: '0.9em' }}>
                {post.authorName}
              </Link>
              {post.isPinned && (
                <Label size="mini" color="blue">
                  <Icon name="pin" style={{ margin: 0 }} />
                </Label>
              )}
              <Label size="mini" basic color={post.visibility === 'Public' ? 'blue' : 'grey'}>
                {post.visibility === 'Public' ? '🌐' : '🔒'}
              </Label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.75em', color: '#aaa' }}>
                {formatDate(post.createdAt)}
              </span>
              {/* Actions dropdown */}
              {(canAdmin || canDelete) && (
                <Popup
                  trigger={<Icon name="ellipsis horizontal" color="grey" style={{ cursor: 'pointer' }} />}
                  content={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120 }}>
                      {canAdmin && (
                        <Button
                          size="mini" basic fluid
                          onClick={() => tournamentStore.pinPost(post.id)}
                        >
                          <Icon name="pin" />
                          {post.isPinned ? t('tournament.chat.unpin') : t('tournament.chat.pin')}
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="mini" basic fluid color="red" onClick={() => setConfirmDelete(true)}>
                          <Icon name="trash" />{t('teams.remove')}
                        </Button>
                      )}
                    </div>
                  }
                  on="click"
                  position="bottom right"
                />
              )}
            </div>
          </div>

          {/* Content */}
          <p style={{ margin: '6px 0', lineHeight: 1.5, wordBreak: 'break-word' }}>{post.content}</p>

          {/* Reactions */}
          <ReactionBar
            reactions={reactions}
            currentUserReaction={currentUserReaction}
            onReact={(type) => tournamentStore.reactToPost(post.id, type)}
          />

          {/* Reply toggle */}
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            {canPost && (
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', fontSize: '0.82em', padding: 0 }}
                onClick={() => { setShowReplyInput(v => !v); }}
              >
                <Icon name="reply" />{t('tournament.chat.reply')}
              </button>
            )}
            {repliesArray.length > 0 && (
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4285f4', fontSize: '0.82em', padding: 0 }}
                onClick={() => setRepliesExpanded(!repliesExpanded)}
              >
                <Icon name={repliesExpanded ? 'angle up' : 'angle down'} />
                {repliesArray.length} {t('tournament.chat.replies')}
              </button>
            )}
          </div>

          {/* Reply input */}
          {showReplyInput && canPost && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
              <TextArea
                value={replyInput}
                onChange={(_, d) => setReplyInput(d.value as string)}
                rows={2}
                placeholder={t('tournament.chat.replyPlaceholder')}
                style={{ flex: 1, resize: 'none', fontSize: '0.88em', padding: 8, borderRadius: 8, border: '1px solid #ddd' }}
              />
              <Button
                size="mini" primary
                disabled={!replyInput.trim() || tournamentStore.submitting}
                loading={tournamentStore.submitting}
                onClick={handleAddReply}
              >
                <Icon name="send" />
              </Button>
            </div>
          )}

          {/* Expanded replies */}
          {repliesExpanded && repliesArray.length > 0 && (
            <div style={{ marginTop: 10, paddingLeft: 10, borderLeft: '2px solid #f0f0f0' }}>
              {repliesArray.map(r => (
                <SingleReply key={r.id} reply={r} postId={post.id} />
              ))}
            </div>
          )}
        </div>
      </div>

      <Confirm
        open={confirmDelete}
        size="mini"
        content={t('tournament.chat.confirmDeletePost')}
        cancelButton={t('teams.cancel')}
        confirmButton={<Button negative size="tiny" content={t('teams.remove')} />}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          await tournamentStore.deletePost(post.id);
          setConfirmDelete(false);
        }}
      />
    </div>
  );
});

// ── Main TournamentChatPanel ──────────────────────────────────────────────────

interface ChatProps {
  contextType: 'Tournament' | 'TournamentMatch' | 'Dispute';
  contextId: string;
  tournament: TournamentDto;
}

export const TournamentChatPanel = observer(function TournamentChatPanel({
  contextType, contextId, tournament,
}: ChatProps) {
  const { tournamentStore, userStore } = useStore();
  const { t } = useTranslation();
  const [newPost, setNewPost] = useState('');
  const [visibility, setVisibility] = useState<TournamentPostVisibility>('ParticipantsOnly');
  const [submitting, setSubmitting] = useState(false);

  const canPost = tournament.isCurrentUserParticipant || tournament.isCurrentUserOrganizer || tournament.isCurrentUserCoOrganizer;
  const canAdmin = tournament.isCurrentUserOrganizer || tournament.isCurrentUserCoOrganizer;

  useEffect(() => {
    if (!contextId || contextId === '00000000-0000-0000-0000-000000000000') {
      return;
  }
    
    tournamentStore.loadPosts(contextType, contextId);
    tournamentStore.setCurrentUserId(userStore.user?.userID);
    
    return () => tournamentStore.clearPosts?.();
  }, [contextType, contextId]); 

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setSubmitting(true);
    await tournamentStore.createPost(contextType, contextId, newPost.trim(), visibility);
    setSubmitting(false);
    setNewPost('');
  };

  const posts = [...tournamentStore.posts].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <Segment style={{ padding: 0, border: 'none', boxShadow: 'none' }}>
      {canPost && (
        <div style={{
          display: 'flex', gap: 10, padding: '12px 0 16px',
          borderBottom: '2px solid #f0f0f0', marginBottom: 8
        }}>
          <div style={{ flex: 1 }}>
            <textarea
              value={newPost}
              onChange={e => setNewPost(e.target.value)}
              placeholder={t('tournament.chat.placeholder')}
              rows={3}
              style={{
                width: '100%', resize: 'none', borderRadius: 10, border: '1px solid #ddd',
                padding: '10px 12px', fontSize: '0.92em', outline: 'none', fontFamily: 'inherit'
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handlePost();
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <Dropdown
                selection compact size="small"
                options={[
                  { key: 'pub', value: 'Public', text: `🌐 ${t('tournament.chat.public')}` },
                  { key: 'part', value: 'ParticipantsOnly', text: `🔒 ${t('tournament.chat.participantsOnly')}` },
                ]}
                value={visibility}
                onChange={(_: React.SyntheticEvent, data: any) => setVisibility(data.value as TournamentPostVisibility)}
              />
              <Button
                primary size="small" disabled={!newPost.trim() || submitting} loading={submitting}
                onClick={handlePost} style={{ borderRadius: 20 }}
              >
                <Icon name="send" />{t('tournament.chat.post')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {!canPost && (
        <Message info style={{ marginBottom: 14 }}>
          <Icon name="info circle" />
          {t('tournament.chat.participantsOnly')}
        </Message>
      )}

      {tournamentStore.loading ? (
        <Dimmer active inverted style={{ minHeight: 100 }}>
          <Loader inverted>{t('teams.loading')}</Loader>
        </Dimmer>
      ) : posts.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '30px 0' }}>
          <Icon name="comments" size="large" />
          <p>{canPost ? t('tournament.chat.noPosts') : t('tournament.chat.notMemberNotice')}</p>
        </div>
      ) : (
        posts.map(post => (
          <SinglePost key={post.id} post={post} canAdmin={canAdmin} canPost={canPost} />
        ))
      )}
    </Segment>
  );
});